import React, { useState, useRef, useEffect } from 'react';
import { Phone } from 'lucide-react';
import { useClickToCall } from '../hooks/useClickToCall';
import { CallerDeviceDialog } from './CallerDeviceDialog';

// Collect distinct usable numbers from a lead-like object.
function collectNumbers(lead) {
  const raw = [
    ['Primary Mobile', lead?.primary_mobile],
    ['Alternate', lead?.alternate_number || lead?.secondary_mobile || lead?.alternate_mobile],
    ['WhatsApp', lead?.whatsapp_number],
    ['Mobile', lead?.mobile]
  ];
  const seen = new Set();
  const out = [];
  for (const [label, value] of raw) {
    const v = (value || '').toString().trim();
    if (v && !seen.has(v)) { seen.add(v); out.push({ label, value: v }); }
  }
  return out;
}

// Reusable GNONS Click-to-Call button. Sends a dial command to the employee's
// registered/default Android device. Never opens a browser tel: link.
export const ClickToCallButton = ({
  lead, phone, leadId, leadName, allowChoose = false,
  className = '', iconOnly = false, label = 'Call', dataTestid = 'click-to-call-btn',
  stopPropagation = false, showStatus = false
}) => {
  const { startCall, dialing, status, selection, setSelection, sendDelegated, previewDelegated } = useClickToCall();
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef(null);

  const resolvedLeadId = leadId ?? lead?.id ?? lead?.lead_id;
  const resolvedLeadName = leadName ?? lead?.customer_name ?? lead?.company_name ?? '';
  const numbers = phone ? [{ label: 'Mobile', value: phone }] : collectNumbers(lead);
  const single = numbers[0]?.value;

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const fire = (e, num) => {
    if (stopPropagation) e.stopPropagation();
    setMenuOpen(false);
    startCall(num, { leadId: resolvedLeadId, leadName: resolvedLeadName });
  };

  const handleClick = (e) => {
    if (stopPropagation) e.stopPropagation();
    if (allowChoose && numbers.length > 1) { setMenuOpen((o) => !o); return; }
    fire(e, single);
  };

  return (
    <span ref={ref} className="relative inline-flex">
      <button type="button" onClick={handleClick} disabled={dialing} data-testid={dataTestid} title="Call via GNONS phone"
        className={className}>
        <Phone className={iconOnly ? 'h-3 w-3' : 'h-4 w-4'} />
        {!iconOnly && <span className="ml-1">{dialing ? 'Calling…' : label}</span>}
      </button>
      {menuOpen && numbers.length > 1 && (
        <div data-testid="click-to-call-number-menu" className="absolute z-50 top-full left-0 mt-1 min-w-[180px] rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#131A2B] shadow-lg py-1">
          {numbers.map((n) => (
            <button key={n.value} type="button" onClick={(e) => fire(e, n.value)} data-testid={`click-to-call-num-${n.value}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800">
              <span className="block font-medium">{n.value}</span>
              <span className="block text-xs text-muted-foreground">{n.label}</span>
            </button>
          ))}
        </div>
      )}
      {showStatus && status && (
        <span data-testid="click-to-call-status" className="ml-2 self-center text-xs text-muted-foreground">
          Device: <span className="font-medium text-primary">{status}</span>
        </span>
      )}
      {selection && (
        <CallerDeviceDialog selection={selection} onClose={() => setSelection(null)}
          onConfirm={sendDelegated} onPreview={previewDelegated} />
      )}
    </span>
  );
};

export default ClickToCallButton;
